'use client'

import * as React from 'react'
import { Fragment } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Check,
  ChevronDown,
  // Plus,
  RefreshCcw,
  X,
} from 'lucide-react'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
  toast,
} from '@ui/web/components'
import { typographyVariants, useSidebarOptional } from '@ui/web/lib'
// import { buildUrl } from '@helper/utils/format'
import { getInitials } from '@helper/utils/format/string'
import cn from '@config/tailwind/cn'

import { PremiumPill } from '../components/premium-pill'
import { useAuthClient, useSharedTRPC } from '../context'

interface SwitchOrganizationProps {
  className?: string
  type: 'school' | 'partner'
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const ROLE_ORDER = ['admin', 'member'] as const
type RoleGroup = (typeof ROLE_ORDER)[number]

const ROLE_HEADING: Record<RoleGroup, string> = {
  admin: 'Admin',
  member: 'Anggota',
}

const ROLE_SUBTITLE: Record<string, Record<'school' | 'partner', string>> = {
  owner: { school: 'Admin Pemilik', partner: 'Pemilik' },
  admin: { school: 'Admin', partner: 'Admin' },
  member: { school: 'Guru', partner: 'Anggota' },
}

export function SwitchOrganization({ className, type, open, onOpenChange }: SwitchOrganizationProps) {
  const sidebar = useSidebarOptional()
  const state = sidebar?.state ?? 'expanded'
  const isMobile = sidebar?.isMobile ?? false

  const authClient = useAuthClient()
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()

  const {
    data: organizations,
    isPending: isPendingOrganizations,
    isError: isErrorOrganizations,
    refetch: refetchOrganizations,
  } = useQuery({ ...trpc.organization.listOrganizations.queryOptions(), staleTime: Infinity })

  const {
    data: currentOrganization,
    isPending: isPendingCurrentOrganization,
    error: errorCurrentOrganization,
    refetch: refetchCurrentOrganization,
  } = authClient.useActiveOrganization()

  const isCollapsed = state === 'collapsed'

  const isPending = isPendingOrganizations || isPendingCurrentOrganization || !currentOrganization || !organizations

  const error = isErrorOrganizations || errorCurrentOrganization

  if (isPending) {
    return (
      <Skeleton
        className={cn(
          'srd:w-full srd:rounded-lg srd:h-10 srd:mx-2 srd:bg-neutral-white srd:border srd:border-outline-variant',
          isCollapsed && 'srd:w-full srd:justify-center srd:px-0 srd:items-center',
          !isMobile && 'srd:max-w-[240px]',
          isMobile && 'srd:max-w-[271px]',
          className,
        )}
      />
    )
  }

  if (error) {
    return (
      <div
        className={cn(
          'srd:w-full srd:px-3 srd:py-2 srd:bg-neutral-white srd:text-neutral-black srd:rounded-lg srd:mx-2 srd:border srd:border-outline-variant srd:flex srd:items-center srd:justify-center srd:gap-x-2',
          isCollapsed && 'srd:w-full srd:justify-center srd:px-0 srd:items-center',
          !isMobile && 'srd:max-w-[240px]',
          isMobile && 'srd:max-w-[271px]',
          'srd:focus:outline-blue-100',
          className,
        )}
      >
        <X className='srd:size-4 srd:text-error' />
        <p className='srd:text-sm srd:text-error'>Error</p>
        <Button
          variant='ghost'
          onClick={async () => {
            await refetchOrganizations()
            await refetchCurrentOrganization()
          }}
          className='srd:p-0 srd:ml-4'
        >
          <RefreshCcw className='srd:size-4 srd:mr-2 srd:text-blue-100' />
          Coba lagi
        </Button>
      </div>
    )
  }

  const handleOrganizationChange = async (value: string | null) => {
    if (!value) {
      toast.error('Tidak ada organisasi yang dipilih')
      return
    }

    const resp = await authClient.organization.setActive({ organizationId: value })

    if (resp.error) {
      toast.error(resp.error.message)
      return
    }

    await queryClient.invalidateQueries()
  }

  const data = organizations.filter((val) => val.type.includes(type))

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentOrganizationData = data.find((val) => val.id === currentOrganization.id)!

  const grouped = ROLE_ORDER.map((roleGroup) => ({
    role: roleGroup,
    heading: ROLE_HEADING[roleGroup],
    items: data.filter((org) => (roleGroup === 'admin' ? org.isAdmin : !org.isAdmin)),
  })).filter((group) => group.items.length > 0)

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger
        render={(props) => (
          <Button
            baseButton
            className={cn(
              'srd:w-full srd:justify-between srd:px-3 srd:py-2 srd:bg-neutral-white srd:text-neutral-black srd:rounded-lg srd:mx-2 srd:border srd:border-outline-variant',
              isCollapsed && 'srd:w-full srd:justify-center srd:px-0 srd:items-center',
              !isMobile && 'srd:max-w-[240px]',
              isMobile && 'srd:max-w-[271px]',
              'srd:focus:outline-blue-100',
              className,
            )}
            {...props}
          >
            <div
              className={cn(
                'srd:flex srd:min-w-0 srd:items-center srd:gap-x-2',
                isCollapsed && 'srd:mx-auto',
                !isCollapsed && 'srd:flex-1',
              )}
            >
              <Avatar
                profile='organization'
                size='sm'
                className={cn('srd:shrink-0', isCollapsed && 'srd:after:hidden')}
              >
                <AvatarImage src={currentOrganizationData.logo ?? undefined} alt={currentOrganizationData.name} />
                <AvatarFallback>{getInitials(currentOrganizationData.name)}</AvatarFallback>
              </Avatar>

              <span className={cn('srd:min-w-0 srd:truncate srd:font-medium', isCollapsed && 'srd:hidden')}>
                {currentOrganizationData.name}
              </span>
            </div>

            <ChevronDown className={cn('srd:size-4 srd:text-blue-100', isCollapsed && 'srd:hidden')} />
          </Button>
        )}
      />

      <DropdownMenuContent
        className='srd:w-(--anchor-width) srd:min-w-[300px] srd:rounded-lg'
        align='start'
        side='bottom'
        sideOffset={4}
      >
        {grouped.map((group, groupIndex) => (
          <Fragment key={group.role}>
            {groupIndex > 0 && <DropdownMenuSeparator />}

            <DropdownMenuGroup>
              <DropdownMenuLabel>{group.heading}</DropdownMenuLabel>

              {group.items.map((val) => (
                <DropdownMenuItem
                  key={`${val.name}-${val.id}-organization`}
                  disabled={val.id === currentOrganization.id}
                  onClick={() => handleOrganizationChange(val.id)}
                  className={cn(
                    val.id === currentOrganization.id && 'srd:focus:bg-transparent srd:data-disabled:opacity-100',
                  )}
                >
                  <Avatar profile='organization'>
                    <AvatarImage src={val.logo ?? undefined} alt={val.name} />
                    <AvatarFallback>{getInitials(val.name)}</AvatarFallback>
                  </Avatar>

                  <div className='srd:flex srd:flex-col srd:min-w-0 srd:flex-1'>
                    <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:truncate')}>{val.name}</span>
                    <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
                      {ROLE_SUBTITLE[val.role]?.[type]}
                    </span>
                  </div>

                  <PremiumPill isPremium={val.isPremium} />

                  {val.id === currentOrganization.id && <Check className='srd:size-4 srd:shrink-0 srd:text-blue-100' />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </Fragment>
        ))}

        {/* {type === 'school' && (
          <>
            <DropdownMenuSeparator />

            <DropdownMenuItem
              className={cn(typographyVariants({ variant: 'label-md' }))}
              onClick={() => (window.location.href = buildUrl('account', '/buat-akun/sekolah').href)}
            >
              <Plus className='srd:size-4' />
              <div className='srd:text-neutral-black srd:font-medium'>Tambah Sekolah Baru</div>
            </DropdownMenuItem>
          </>
        )} */}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
